import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ThumbnailList } from '../ThumbnailList';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../Accordion';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';

const QuizStatusIcon = ({ quizStatus }: { quizStatus?: { totalQuestions: number; answeredCount: number } }) => {
  if (!quizStatus || quizStatus.totalQuestions === 0) {
    return null;
  }

  const { totalQuestions, answeredCount } = quizStatus;
  const allAnswered = answeredCount >= totalQuestions;

  if (allAnswered) {
    return (
      <Tooltip>
        <TooltipContent>All {totalQuestions} question{totalQuestions > 1 ? 's' : ''} answered</TooltipContent>
        <TooltipTrigger asChild>
          <div className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-green-600 flex-shrink-0">
            <span className="text-[10px] font-bold text-white">&#10003;</span>
          </div>
        </TooltipTrigger>
      </Tooltip>
    );
  }

  if (answeredCount === 0) {
    return (
      <Tooltip>
        <TooltipContent>{totalQuestions} question{totalQuestions > 1 ? 's' : ''} available</TooltipContent>
        <TooltipTrigger asChild>
          <div className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-amber-500 flex-shrink-0">
            <span className="text-[10px] font-bold text-white">?</span>
          </div>
        </TooltipTrigger>
      </Tooltip>
    );
  }

  // Partial — some answered
  return (
    <Tooltip>
      <TooltipContent>{answeredCount}/{totalQuestions} question{totalQuestions > 1 ? 's' : ''} answered</TooltipContent>
      <TooltipTrigger asChild>
        <div className="flex h-[18px] min-w-[18px] px-1 items-center justify-center rounded-full bg-amber-500 flex-shrink-0">
          <span className="text-[9px] font-bold text-white">{answeredCount}/{totalQuestions}</span>
        </div>
      </TooltipTrigger>
    </Tooltip>
  );
};

const StudyItem = ({
  date,
  description,
  numInstances,
  modalities,
  patientName,
  isActive,
  onClick,
  isExpanded,
  displaySets,
  activeDisplaySetInstanceUIDs,
  onClickThumbnail,
  onDoubleClickThumbnail,
  onClickUntrack,
  viewPreset = 'thumbnails',
  ThumbnailMenuItems,
  StudyMenuItems,
  StudyInstanceUID,
  onEditDescription,
  quizStatus,
  isViewed = true,
}: withAppTypes) => {
  return (
    <Accordion
      type="single"
      collapsible
      onClick={onClick}
      onKeyDown={() => {}}
      role="button"
      tabIndex={0}
      value={isExpanded ? 'study-item' : ''}
      onValueChange={() => {
        // Controlled: parent's onClick handler (above) drives expansion state
        // via expandedStudyInstanceUIDs. Keeping Radix's open/closed in sync
        // with that state — instead of letting it auto-open on isActive — is
        // what lets the worklist row stay collapsed while still showing the
        // blue "active" highlight.
      }}
    >
      <AccordionItem value="study-item">
        <AccordionTrigger className={classnames('hover:bg-accent bg-popover group w-full rounded', {
          'border-l-4 border-l-blue-500 bg-blue-900/20': isActive, // Highlight active study
        })}>
          <div className={classnames('flex w-full flex-row overflow-hidden py-1', {
            'min-h-[56px]': patientName, // Taller when showing patient name
            'h-[40px]': !patientName, // Original height without patient name
          })}>
            <div className="flex w-full flex-row items-center justify-between">
              <div className="flex items-center gap-[6px] min-w-0">
                {/* Unopened dot indicator */}
                {!isViewed && (
                  <div className="h-[7px] w-[7px] rounded-full bg-blue-400 flex-shrink-0" title="Not yet viewed" />
                )}
                {/* Quiz status icon */}
                <QuizStatusIcon quizStatus={quizStatus} />
                <div className="flex min-w-0 flex-col items-start text-[13px]">
                  {patientName && (
                    <Tooltip>
                      <TooltipContent>{patientName}</TooltipContent>
                      <TooltipTrigger
                        className="w-full"
                        asChild
                      >
                        <div className={classnames(
                          'h-[18px] w-full max-w-[160px] overflow-hidden truncate whitespace-nowrap text-left text-[11px]',
                          !isViewed ? 'text-white font-bold' : 'text-white font-medium'
                        )}>
                          {patientName}
                        </div>
                      </TooltipTrigger>
                    </Tooltip>
                  )}
                  <Tooltip>
                    <TooltipContent>{date}</TooltipContent>
                    <TooltipTrigger className="w-full" asChild>
                      <div className={classnames(
                        'text-[12px] whitespace-nowrap',
                        !isViewed ? 'text-white font-semibold' : 'text-white'
                      )}>{date}</div>
                    </TooltipTrigger>
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* Bottom Row: Modality • Description (left) | Count (right) */}
            <div className="flex w-full items-center justify-between pl-[18px]">
              <div className="flex items-center gap-[6px] min-w-0 flex-1">
                <div className={classnames(
                  'text-[11px] whitespace-nowrap',
                  !isViewed ? 'text-muted-foreground font-semibold' : 'text-muted-foreground'
                )}>{modalities?.substring(0, 2)}</div>
                <div className="text-muted-foreground">•</div>
                <Tooltip>
                  <TooltipContent>{description}</TooltipContent>
                  <TooltipTrigger className="min-w-0" asChild>
                    <div className={classnames(
                      'text-[11px] truncate',
                      !isViewed ? 'text-muted-foreground font-semibold' : 'text-muted-foreground'
                    )}>{description}</div>
                  </TooltipTrigger>
                </Tooltip>
              </div>
              <div className="text-[11px] text-muted-foreground ml-2 whitespace-nowrap">{numInstances}</div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent
          onClick={event => {
            event.stopPropagation();
          }}
        >
          {isExpanded && displaySets && (
            <ThumbnailList
              thumbnails={displaySets}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              onThumbnailClick={onClickThumbnail}
              onThumbnailDoubleClick={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              onEditDescription={onEditDescription}
            />
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

StudyItem.propTypes = {
  date: PropTypes.string.isRequired,
  description: PropTypes.string,
  modalities: PropTypes.string.isRequired,
  numInstances: PropTypes.number.isRequired,
  patientName: PropTypes.string,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
  displaySets: PropTypes.array,
  activeDisplaySetInstanceUIDs: PropTypes.array,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  viewPreset: PropTypes.string,
  StudyMenuItems: PropTypes.func,
  StudyInstanceUID: PropTypes.string,
  onEditDescription: PropTypes.func,
  quizStatus: PropTypes.shape({
    totalQuestions: PropTypes.number,
    answeredCount: PropTypes.number,
  }),
  isViewed: PropTypes.bool,
};

export { StudyItem };
